import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Plus, Trash, X, PencilSimple, MagnifyingGlass, AddressBook, Phone, EnvelopeSimple, FileText, BuildingOffice, Stack, Path, CheckCircle, Truck as TruckIcon } from "@phosphor-icons/react";

const ROLES = [
  { key: "both",      label: "Both",      cls: "bg-slate-100 text-slate-700 border-slate-200" },
  { key: "sender",    label: "Sender",    cls: "bg-sky-50 text-ps-blue border-sky-200" },
  { key: "consignee", label: "Consignee", cls: "bg-orange-50 text-ps-orange border-orange-200" },
];
const roleCls = (r) => (ROLES.find((x) => x.key === r) || ROLES[0]).cls;

const EMPTY = {
  name: "",
  address: "",
  role: "both",
  contact_name: "",
  contact_phone: "",
  contact_email: "",
  default_waybill_source: "portside",
  default_client_waybill_issuer: "",
  notes: "",
  // Billing
  default_billing_mode: "per_trip",
  rate_per_trip: "",
  rate_per_tonne: "",
  waiting_day_rate: "",
  rate_currency: "GHS",
  default_activity_type: "loading",
};

const CURRENCIES = [
  { v: "GHS", label: "GHS · Ghana cedi" },
  { v: "XOF", label: "XOF · CFA franc" },
  { v: "USD", label: "USD · US dollar" },
  { v: "EUR", label: "EUR · Euro" },
];

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (roleFilter) params.role = roleFilter;
    const { data } = await api.get("/customers", { params });
    setRows(data);
    setLoading(false);
    loadLaneCounts(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [roleFilter]);

  const openNew = () => { setEditingId(null); setForm(EMPTY); setErr(""); setOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name, address: c.address || "",
      role: c.role || "both",
      contact_name: c.contact_name || "",
      contact_phone: c.contact_phone || "",
      contact_email: c.contact_email || "",
      default_waybill_source: c.default_waybill_source || "portside",
      default_client_waybill_issuer: c.default_client_waybill_issuer || "",
      notes: c.notes || "",
      default_billing_mode: c.default_billing_mode || "per_trip",
      rate_per_trip: c.rate_per_trip ?? "",
      rate_per_tonne: c.rate_per_tonne ?? "",
      waiting_day_rate: c.waiting_day_rate ?? "",
      rate_currency: c.rate_currency || "GHS",
      default_activity_type: c.default_activity_type || "loading",
    });
    setErr("");
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      // Coerce numeric fields ("" → null so the backend keeps None)
      const payload = {
        ...form,
        rate_per_trip:    form.rate_per_trip    === "" ? null : parseFloat(form.rate_per_trip),
        rate_per_tonne:   form.rate_per_tonne   === "" ? null : parseFloat(form.rate_per_tonne),
        waiting_day_rate: form.waiting_day_rate === "" ? null : parseFloat(form.waiting_day_rate),
      };
      if (editingId) {
        await api.patch(`/customers/${editingId}`, payload);
      } else {
        await api.post("/customers", payload);
      }
      setOpen(false);
      setForm(EMPTY);
      setEditingId(null);
      await load();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  const [lanesFor, setLanesFor] = useState(null); // {customer} for the Lanes manager
  const [laneCounts, setLaneCounts] = useState({}); // {customerId: laneCount}

  const loadLaneCounts = async (customerRows) => {
    const targets = customerRows.filter((c) => c.is_contract || c.role === "sender" || c.role === "both").slice(0, 100);
    const results = await Promise.all(targets.map((c) => api.get(`/customers/${c.id}/lanes`).then(({ data }) => [c.id, data.length]).catch(() => [c.id, 0])));
    setLaneCounts(Object.fromEntries(results));
  };

  const del = async (c) => {
    if (!window.confirm(`Delete customer ${c.name}?\nThis will not affect existing trips.`)) return;
    await api.delete(`/customers/${c.id}`);
    await load();
  };

  const backfill = async () => {
    if (!window.confirm("Scan all existing trips and add any missing senders/consignees to the address book?")) return;
    const { data } = await api.post("/customers/backfill-from-trips");
    await load();
    window.alert(`Added ${data.inserted_senders} senders and ${data.inserted_consignees} consignees.`);
  };

  return (
    <div data-testid="customers-page">
      <PageHeader
        subtitle="Address book"
        title="Customers"
        action={
          <div className="flex gap-2">
            <button
              onClick={backfill}
              data-testid="customers-backfill"
              className="border border-slate-300 bg-white text-ps-deep px-3 py-2.5 rounded-md font-semibold flex items-center gap-2 hover:bg-slate-50 text-sm"
              title="Import senders & consignees from existing trips"
            >
              <AddressBook size={16} weight="bold" /> Import from trips
            </button>
            <button onClick={openNew} data-testid="new-customer-button" className="bg-ps-blue text-white px-4 py-2.5 rounded-md font-semibold flex items-center gap-2 hover:bg-ps-deep transition-colors shadow-sm">
              <Plus size={16} weight="bold" /> New customer
            </button>
          </div>
        }
      />

      {/* Filter strip */}
      <div className="px-8 py-4 bg-slate-100 border-b border-slate-200 flex gap-4 items-end flex-wrap">
        <div className="relative w-72">
          <MagnifyingGlass size={14} className="absolute left-3 top-3 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="customers-search"
            placeholder="Search name, address, contact…"
            className="w-full border border-slate-300 bg-white pl-8 pr-3 py-2 text-sm font-mono-data"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Role</label>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            data-testid="customers-role-filter"
            className="border border-slate-300 bg-white px-2 py-2 text-sm font-mono-data"
          >
            <option value="">All</option>
            <option value="sender">Senders (incl. both)</option>
            <option value="consignee">Consignees (incl. both)</option>
            <option value="both">Both only</option>
          </select>
        </div>
        <button onClick={load} className="text-xs uppercase tracking-wider font-bold text-ps-blue hover:text-ps-deep">Apply</button>
        <div className="ml-auto text-right">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Customers</div>
          <div className="font-mono-data tabular text-xl text-ps-deep">{rows.length}</div>
        </div>
      </div>

      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 text-left">Name</th>
                <th className="px-3 py-3 text-left">Role</th>
                <th className="px-3 py-3 text-left">Address</th>
                <th className="px-3 py-3 text-left">Preferred contact</th>
                <th className="px-3 py-3 text-left">Waybill default</th>
                <th className="px-3 py-3 text-left">Lanes</th>
                <th className="px-3 py-3 text-right">Used</th>
                <th className="px-3 py-3 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-testid="customers-table-body">
              {loading && <tr><td colSpan={8} className="p-6"><div className="indus-bar w-1/3" /></td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="p-12 text-center text-slate-500">
                  No customers yet. Click <span className="font-semibold text-ps-deep">Import from trips</span> to auto-populate from existing orders, or <span className="font-semibold text-ps-deep">New customer</span>.
                </td></tr>
              )}
              {rows.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                  <td className="px-3 py-2 font-semibold text-ps-deep">
                    <div className="flex items-center gap-2">
                      <BuildingOffice size={14} weight="duotone" className="text-slate-400" />
                      {c.name}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${roleCls(c.role)}`}>{c.role}</span>
                  </td>
                  <td className="px-3 py-2 text-slate-600 max-w-[260px] truncate" title={c.address}>{c.address || "—"}</td>
                  <td className="px-3 py-2">
                    {c.contact_name && <div className="font-semibold">{c.contact_name}</div>}
                    {c.contact_phone && <div className="text-xs text-slate-500 font-mono-data flex items-center gap-1"><Phone size={10} /> {c.contact_phone}</div>}
                    {c.contact_email && <div className="text-xs text-slate-500 flex items-center gap-1"><EnvelopeSimple size={10} /> {c.contact_email}</div>}
                    {!(c.contact_name || c.contact_phone || c.contact_email) && <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    {c.default_waybill_source === "client" ? (
                      <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border bg-amber-50 text-amber-700 border-amber-200">Client own</span>
                    ) : (
                      <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border bg-sky-50 text-ps-blue border-sky-200">Portside CMR</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => setLanesFor(c)}
                      data-testid={`customer-lanes-${c.id}`}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-200 hover:border-ps-blue hover:bg-sky-50 transition-colors"
                      title="Manage service lanes for this customer"
                    >
                      <Stack size={11} weight="bold" className="text-ps-blue" />
                      <span className="font-mono-data tabular text-ps-deep font-semibold">{laneCounts[c.id] ?? "—"}</span>
                      <span className="text-slate-500">lanes</span>
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right font-mono-data tabular text-slate-600">{c.usage_count}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => openEdit(c)} data-testid={`customer-edit-${c.id}`} title="Edit" className="text-slate-400 hover:text-ps-blue p-1 rounded hover:bg-sky-50"><PencilSimple size={14} weight="bold" /></button>
                      <button onClick={() => del(c)} data-testid={`customer-delete-${c.id}`} title="Delete" className="text-slate-400 hover:text-red-600 p-1"><Trash size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex justify-end" onClick={() => setOpen(false)} data-testid="customer-dialog">
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white h-full overflow-y-auto border-l border-slate-200">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{editingId ? "Edit customer" : "Address book"}</div>
                <h2 className="font-heading font-black text-2xl tracking-tight">{editingId ? form.name : "New customer"}</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-500"><X size={20} /></button>
            </div>

            <div className="p-6 space-y-4">
              <Field label="Company name *" testid="customer-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required placeholder="e.g. De Heus West Africa" />

              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Role *</label>
                <div className="grid grid-cols-3 gap-2" data-testid="customer-role-toggle">
                  {ROLES.map((r) => {
                    const active = form.role === r.key;
                    return (
                      <button
                        key={r.key}
                        type="button"
                        onClick={() => setForm({ ...form, role: r.key })}
                        data-testid={`customer-role-${r.key}`}
                        className={`border rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                          active ? "border-ps-blue bg-sky-50 text-ps-deep" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <Field label="Address" testid="customer-address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} placeholder="Street, city, country" />

              <div className="border-t border-slate-200 pt-4 mt-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2 font-semibold">Preferred contact</div>
                <div className="space-y-3">
                  <Field label="Contact person" testid="customer-contact-name" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} placeholder="e.g. Kwame Mensah" />
                  <Field label="Mobile" testid="customer-contact-phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} placeholder="+233 24 …" />
                  <Field label="E-mail" type="email" testid="customer-contact-email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} placeholder="name@company.com" />
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4 mt-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2 font-semibold">Waybill defaults (sender)</div>
                <div className="grid grid-cols-2 gap-2" data-testid="customer-waybill-toggle">
                  {[
                    { v: "portside", label: "Portside CMR", hint: "We issue & print" },
                    { v: "client",   label: "Client waybill", hint: "They use their own" },
                  ].map((o) => {
                    const active = (form.default_waybill_source || "portside") === o.v;
                    return (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setForm({ ...form, default_waybill_source: o.v })}
                        data-testid={`customer-waybill-${o.v}`}
                        className={`text-left border rounded-md px-3 py-2 text-sm transition-colors ${
                          active ? "border-ps-blue bg-sky-50 text-ps-deep" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <div className="font-semibold flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${active ? "bg-ps-blue" : "bg-slate-300"}`} />
                          {o.label}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5">{o.hint}</div>
                      </button>
                    );
                  })}
                </div>
                <div className="text-[10px] text-slate-500 mt-2 flex items-start gap-1">
                  <FileText size={11} className="mt-0.5 shrink-0" />
                  <span>When this customer is picked as <strong>sender</strong> in a CMR, the waybill source toggle will auto-flip to this default.</span>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4 mt-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2 font-semibold">Billing rates</div>
                <div className="grid grid-cols-2 gap-2 mb-3" data-testid="customer-billing-mode-toggle">
                  {[
                    { v: "per_trip",  label: "Per trip",   hint: "Charged per delivery" },
                    { v: "per_tonne", label: "Per tonne",  hint: "Charged per m/t carried" },
                  ].map((o) => {
                    const active = (form.default_billing_mode || "per_trip") === o.v;
                    return (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setForm({ ...form, default_billing_mode: o.v })}
                        data-testid={`customer-billing-mode-${o.v}`}
                        className={`text-left border rounded-md px-3 py-2 text-sm transition-colors ${active ? "border-ps-blue bg-sky-50 text-ps-deep" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}
                      >
                        <div className="font-semibold flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${active ? "bg-ps-blue" : "bg-slate-300"}`} />
                          {o.label}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5">{o.hint}</div>
                      </button>
                    );
                  })}
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Currency</label>
                  <select
                    value={form.rate_currency || "GHS"}
                    onChange={(e) => setForm({ ...form, rate_currency: e.target.value })}
                    data-testid="customer-rate-currency"
                    className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-ps-blue bg-white"
                  >
                    {CURRENCIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <Field label={`Rate / trip (${form.rate_currency || "GHS"})`} type="number" step="0.01" min="0" testid="customer-rate-per-trip" value={form.rate_per_trip} onChange={(v) => setForm({ ...form, rate_per_trip: v })} placeholder="e.g. 850" />
                  <Field label={`Rate / tonne (${form.rate_currency || "GHS"})`} type="number" step="0.01" min="0" testid="customer-rate-per-tonne" value={form.rate_per_tonne} onChange={(v) => setForm({ ...form, rate_per_tonne: v })} placeholder="e.g. 45" />
                </div>
                <div className="mt-3">
                  <Field label={`Waiting day rate (${form.rate_currency || "GHS"} per day)`} type="number" step="0.01" min="0" testid="customer-waiting-day-rate" value={form.waiting_day_rate} onChange={(v) => setForm({ ...form, waiting_day_rate: v })} placeholder="e.g. 200" />
                </div>
                <div className="mt-4">
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Default activity type</label>
                  <div className="grid grid-cols-2 gap-2" data-testid="customer-default-activity-type">
                    {[
                      { v: "loading",    label: "Loading",    hint: "Yard → out" },
                      { v: "evacuation", label: "Evacuation", hint: "Containers (Arkas, Hapag)" },
                      { v: "trip",       label: "Trip",       hint: "Point-to-point (Bunge, De Heus)" },
                      { v: "other",      label: "Other",      hint: "Anything else" },
                    ].map((o) => {
                      const active = (form.default_activity_type || "loading") === o.v;
                      return (
                        <button
                          key={o.v}
                          type="button"
                          onClick={() => setForm({ ...form, default_activity_type: o.v })}
                          data-testid={`customer-default-activity-${o.v}`}
                          className={`text-left border rounded-md px-3 py-2 text-sm transition-colors ${active ? "border-ps-blue bg-sky-50 text-ps-deep" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}
                        >
                          <div className="font-semibold flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${active ? "bg-ps-blue" : "bg-slate-300"}`} />
                            {o.label}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5">{o.hint}</div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-2">Pre-fills the activity type when a dispatcher logs a trip for this customer.</div>
                </div>
                <div className="text-[10px] text-slate-500 mt-2">Operator may still override per-trip vs per-tonne when logging a specific activity.</div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Internal notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="customer-notes"
                  rows={2}
                  className="w-full border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Anything we should remember…"
                />
              </div>

              {err && <div className="text-sm text-ps-red bg-red-50 border border-red-200 rounded p-2" data-testid="customer-error">{err}</div>}
            </div>

            <div className="p-6 border-t border-slate-200 flex gap-2 bg-slate-50 sticky bottom-0">
              <button type="button" onClick={() => setOpen(false)} className="flex-1 border border-slate-300 py-2.5 text-sm hover:bg-slate-100">Cancel</button>
              <button type="submit" disabled={saving} data-testid="customer-submit" className="flex-1 bg-ps-blue text-white py-2.5 text-sm font-semibold hover:bg-ps-deep disabled:opacity-60">
                {saving ? "Saving…" : editingId ? "Save changes" : "Create customer"}
              </button>
            </div>
          </form>
        </div>
      )}

      {lanesFor && (
        <LanesManager
          customer={lanesFor}
          onClose={() => { setLanesFor(null); load(); }}
        />
      )}
    </div>
  );
}

function Field({ label, testid, type = "text", value, onChange, ...rest }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-ps-blue"
        {...rest}
      />
    </div>
  );
}

const CURRENCY_OPTS = ["GHS", "XOF", "USD", "EUR"];

const EMPTY_LANE = {
  name: "",
  origin: "",
  destination: "",
  billing_mode: "per_trip",
  rate_per_unit: "",
  rate_currency: "GHS",
  waiting_day_rate: "",
  default_fuel_per_trip_l: "",
  notes: "",
};

export function LanesManager({ customer, onClose }) {
  const [lanes, setLanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view; "new" or laneId = form view
  const [form, setForm] = useState(EMPTY_LANE);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/customers/${customer.id}/lanes?include_inactive=true`);
    setLanes(data || []);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customer.id]);

  const openNew = () => {
    setForm({ ...EMPTY_LANE, rate_currency: customer.rate_currency || "GHS" });
    setErr("");
    setEditing("new");
  };
  const openEdit = (lane) => {
    setForm({
      name: lane.name,
      origin: lane.origin || "",
      destination: lane.destination || "",
      billing_mode: lane.billing_mode,
      rate_per_unit: lane.rate_per_unit ?? "",
      rate_currency: lane.rate_currency || "GHS",
      waiting_day_rate: lane.waiting_day_rate ?? "",
      default_fuel_per_trip_l: lane.default_fuel_per_trip_l ?? "",
      notes: lane.notes || "",
    });
    setErr("");
    setEditing(lane.id);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        origin: form.origin.trim(),
        destination: form.destination.trim(),
        billing_mode: form.billing_mode,
        rate_per_unit:           form.rate_per_unit           === "" ? null : parseFloat(form.rate_per_unit),
        rate_currency: form.rate_currency,
        waiting_day_rate:        form.waiting_day_rate        === "" ? null : parseFloat(form.waiting_day_rate),
        default_fuel_per_trip_l: form.default_fuel_per_trip_l === "" ? null : parseFloat(form.default_fuel_per_trip_l),
        notes: form.notes.trim(),
      };
      if (editing === "new") {
        await api.post(`/customers/${customer.id}/lanes`, body);
      } else {
        await api.patch(`/lanes/${editing}`, body);
      }
      setEditing(null);
      await load();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (lane) => {
    try {
      await api.patch(`/lanes/${lane.id}`, { is_active: !lane.is_active });
      await load();
    } catch (e) { window.alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const removeLane = async (lane) => {
    if (!window.confirm(`Soft-delete lane '${lane.name}'? It will be hidden but historical activities keep their billing snapshot.`)) return;
    try {
      await api.delete(`/lanes/${lane.id}`);
      await load();
    } catch (e) { window.alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4" onClick={onClose} data-testid="lanes-manager">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
          <Stack size={22} weight="duotone" className="text-ps-blue" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.25em] text-ps-blue font-semibold">Service lanes for</div>
            <h2 className="font-display font-extrabold text-2xl text-ps-deep tracking-tight">{customer.name}</h2>
            <p className="text-xs text-slate-500 mt-0.5">One customer, multiple work streams (per-trip OR per-tonne). Operators pick a lane at dispatch — its rate is snapshotted onto the activity.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-ps-deep p-1" data-testid="lanes-close"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {editing ? (
            <form onSubmit={submit} className="space-y-3">
              <button type="button" onClick={() => { setEditing(null); setErr(""); }} className="text-xs text-slate-500 hover:text-ps-deep" data-testid="lane-form-back">← back</button>
              <Field label="Lane name *" testid="lane-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required placeholder="Shea butter · Bobo → Techiman" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Origin" testid="lane-origin" value={form.origin} onChange={(v) => setForm({ ...form, origin: v })} placeholder="BOBO" />
                <Field label="Destination" testid="lane-destination" value={form.destination} onChange={(v) => setForm({ ...form, destination: v })} placeholder="TECHIMAN" />
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Billing mode</label>
                <div className="grid grid-cols-2 gap-2" data-testid="lane-billing-mode-toggle">
                  {[
                    { v: "per_trip",  label: "Per trip" },
                    { v: "per_tonne", label: "Per tonne" },
                  ].map((o) => {
                    const active = form.billing_mode === o.v;
                    return (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setForm({ ...form, billing_mode: o.v })}
                        data-testid={`lane-billing-mode-${o.v}`}
                        className={`border rounded-md px-3 py-2 text-sm font-semibold transition-colors ${active ? "border-ps-blue bg-sky-50 text-ps-deep" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Currency</label>
                <select
                  value={form.rate_currency}
                  onChange={(e) => setForm({ ...form, rate_currency: e.target.value })}
                  data-testid="lane-currency"
                  className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-ps-blue bg-white"
                >
                  {CURRENCY_OPTS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field
                  label={`Rate / ${form.billing_mode === "per_tonne" ? "tonne" : "trip"} (${form.rate_currency})`}
                  type="number" step="0.01" min="0"
                  testid="lane-rate"
                  value={form.rate_per_unit}
                  onChange={(v) => setForm({ ...form, rate_per_unit: v })}
                  placeholder={form.billing_mode === "per_tonne" ? "45" : "1200"}
                />
                <Field
                  label={`Waiting day rate (${form.rate_currency}/d)`}
                  type="number" step="0.01" min="0"
                  testid="lane-waiting-day-rate"
                  value={form.waiting_day_rate}
                  onChange={(v) => setForm({ ...form, waiting_day_rate: v })}
                  placeholder="200"
                />
              </div>

              <Field
                label="Default fuel per trip (L)"
                type="number" step="1" min="0"
                testid="lane-fuel"
                value={form.default_fuel_per_trip_l}
                onChange={(v) => setForm({ ...form, default_fuel_per_trip_l: v })}
                placeholder={customer.default_fuel_per_trip_l || "250"}
              />

              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  data-testid="lane-notes"
                  className="w-full border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Tanker lane, sometimes overnight"
                />
              </div>

              {editing !== "new" && (() => {
                const history = (lanes.find((l) => l.id === editing)?.rate_history || []).slice().reverse();
                if (history.length === 0) return null;
                const fmtVal = (val, cur, mode) => (val == null || val === "") ? "—" : `${val} ${cur || ""}${mode ? ` / ${mode === "per_tonne" ? "t" : "trip"}` : ""}`;
                return (
                  <details className="border border-slate-200 rounded bg-slate-50/50" data-testid="lane-rate-history">
                    <summary className="cursor-pointer px-3 py-2 text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold hover:bg-slate-100">
                      Rate history · {history.length} change{history.length === 1 ? "" : "s"}
                    </summary>
                    <div className="px-3 pb-3 pt-1 space-y-2">
                      {history.map((h, i) => {
                        const rateChanged = h.old_rate !== h.new_rate || h.old_currency !== h.new_currency || h.old_mode !== h.new_mode;
                        const waitChanged = (h.old_waiting_day_rate ?? null) !== (h.new_waiting_day_rate ?? null);
                        return (
                          <div key={i} className="text-[11px] border-l-2 border-ps-blue pl-2 py-0.5" data-testid={`lane-rate-history-entry-${i}`}>
                            <div className="text-slate-500 font-mono-data">
                              {h.at ? new Date(h.at).toLocaleString() : ""} · {h.by_name || h.by_email || "—"}
                            </div>
                            {rateChanged && (
                              <div className="text-ps-deep font-mono-data">
                                Rate: <span className="line-through text-slate-400">{fmtVal(h.old_rate, h.old_currency, h.old_mode)}</span>
                                <span className="mx-1">→</span>
                                <span className="font-semibold">{fmtVal(h.new_rate, h.new_currency, h.new_mode)}</span>
                              </div>
                            )}
                            {waitChanged && (
                              <div className="text-ps-deep font-mono-data">
                                Waiting/day: <span className="line-through text-slate-400">{fmtVal(h.old_waiting_day_rate, h.old_currency)}</span>
                                <span className="mx-1">→</span>
                                <span className="font-semibold">{fmtVal(h.new_waiting_day_rate, h.new_currency)}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <div className="text-[10px] text-slate-400 pt-1 italic">Past trips keep the rate they were logged with — history is informational.</div>
                    </div>
                  </details>
                );
              })()}

              {err && <div className="text-sm text-ps-red bg-red-50 border border-red-200 rounded p-2" data-testid="lane-error">{err}</div>}

              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setEditing(null)} className="flex-1 border border-slate-300 py-2.5 text-sm hover:bg-slate-100 rounded">Cancel</button>
                <button type="submit" disabled={saving || !form.name.trim()} data-testid="lane-submit" className="flex-1 bg-ps-blue text-white py-2.5 text-sm font-semibold hover:bg-ps-deep disabled:opacity-60 rounded">
                  {saving ? "Saving…" : editing === "new" ? "Create lane" : "Save changes"}
                </button>
              </div>
            </form>
          ) : (
            <>
              {loading && <div className="indus-bar w-1/3" />}
              {!loading && lanes.length === 0 && (
                <div className="text-center py-8" data-testid="lanes-empty">
                  <Path size={32} className="text-slate-300 mx-auto mb-2" />
                  <div className="font-semibold text-ps-deep">No lanes yet</div>
                  <div className="text-sm text-slate-500 mt-1">Add a lane to model a distinct service line (e.g. tanker route per-tonne, container shunt per-trip).</div>
                </div>
              )}
              {!loading && lanes.length > 0 && (
                <div className="space-y-2" data-testid="lanes-list">
                  {lanes.map((lane) => (
                    <div key={lane.id} className={`border rounded-md p-3 flex items-start gap-3 hover:border-ps-blue transition-colors ${lane.is_active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-70"}`} data-testid={`lane-row-${lane.id}`}>
                      <div className={`w-9 h-9 grid place-items-center rounded ${lane.billing_mode === "per_tonne" ? "bg-amber-50 text-ps-orange" : "bg-sky-50 text-ps-blue"}`}>
                        {lane.billing_mode === "per_tonne" ? <TruckIcon size={18} weight="duotone" /> : <Path size={18} weight="duotone" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-semibold text-ps-deep">{lane.name}</div>
                          <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded border ${lane.billing_mode === "per_tonne" ? "bg-amber-50 text-ps-orange border-amber-200" : "bg-sky-50 text-ps-blue border-sky-200"}`}>{lane.billing_mode === "per_tonne" ? "Per tonne" : "Per trip"}</span>
                          {!lane.is_active && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded border bg-slate-200 text-slate-600 border-slate-300">Inactive</span>}
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5 font-mono-data">
                          {lane.rate_per_unit ? <>{lane.rate_per_unit} {lane.rate_currency} / {lane.billing_mode === "per_tonne" ? "t" : "trip"}</> : <span className="text-amber-700">no rate</span>}
                          {lane.waiting_day_rate ? <> · {lane.waiting_day_rate} {lane.rate_currency}/day wait</> : null}
                          {lane.default_fuel_per_trip_l ? <> · default {lane.default_fuel_per_trip_l} L</> : null}
                        </div>
                        {(lane.origin || lane.destination) && (
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            {[lane.origin, lane.destination].filter(Boolean).join(" → ")}
                          </div>
                        )}
                        <div className="text-[10px] text-slate-400 mt-1">Used {lane.usage_count} time{lane.usage_count === 1 ? "" : "s"}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => toggleActive(lane)} title={lane.is_active ? "Mark inactive" : "Mark active"} className="text-slate-400 hover:text-ps-blue p-1 rounded hover:bg-sky-50" data-testid={`lane-toggle-${lane.id}`}>
                          <CheckCircle size={14} weight={lane.is_active ? "fill" : "bold"} className={lane.is_active ? "text-lime-600" : ""} />
                        </button>
                        <button onClick={() => openEdit(lane)} title="Edit" className="text-slate-400 hover:text-ps-blue p-1 rounded hover:bg-sky-50" data-testid={`lane-edit-${lane.id}`}><PencilSimple size={14} weight="bold" /></button>
                        <button onClick={() => removeLane(lane)} title="Delete" className="text-slate-400 hover:text-red-600 p-1" data-testid={`lane-delete-${lane.id}`}><Trash size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {!editing && (
          <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex justify-end">
            <button onClick={openNew} data-testid="lanes-new" className="bg-ps-blue text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] rounded-md hover:bg-ps-deep inline-flex items-center gap-2">
              <Plus size={14} weight="bold" /> Add lane
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
